import { COLORS, FONTS } from "../../../../../lib/brand";
import { LogoMark } from "../../../../../components/brand/Logo";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Image,
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Svg, { Circle, Line, Path, Rect } from "react-native-svg";

import { getClientCompetition } from "../../../../../lib/clientCompetitions";
import { exportToCsv } from "../../../../../lib/csv";
import { useRequireApprovedCase } from "../../../../../lib/useRequireApprovedCase";
import {
  listMyShortlist,
  removeFromShortlist,
  updateShortlistNotes,
  type ShortlistRow,
} from "../../../../../lib/shortlist";
import type { ClientCompetitionListRow } from "../../../../../types/database";

const THEME = PANE_THEMES.Client;
const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;
const RED = THEME.destructive;
const RED_BG = "rgba(220,80,80,0.08)";
const RED_BORDER = "rgba(220,80,80,0.40)";
const RED_TEXT = "rgba(255,180,180,0.92)";

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

const PAGE_MAX_W = 1240;
const SORT_KEYS = ["recent", "name", "submissions", "note"] as const;
type SortKey = (typeof SORT_KEYS)[number];
const SORT_LABELS: Record<SortKey, string> = {
  recent: "Most recent",
  name: "By name",
  submissions: "By submissions",
  note: "By note",
};

const NOTE_AUTOSAVE_MS = 1500;

function makeInitials(s: string | null | undefined): string {
  if (!s?.trim()) return "??";
  const parts = s.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function shortNote(s: string | null, max = 80): string {
  if (!s?.trim()) return "";
  const t = s.trim().replace(/\s+/g, " ");
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function readSort(competitionId: string): SortKey {
  if (Platform.OS !== "web" || typeof window === "undefined") return "recent";
  try {
    const raw = window.localStorage.getItem(`coravu.shortlist.${competitionId}.sort`);
    if (raw && (SORT_KEYS as readonly string[]).includes(raw)) return raw as SortKey;
  } catch {
    /* private mode / storage disabled */
  }
  return "recent";
}

function writeSort(competitionId: string, sort: SortKey) {
  if (Platform.OS !== "web" || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`coravu.shortlist.${competitionId}.sort`, sort);
  } catch {
    /* ignore */
  }
}

/* ─────────────────────────  Page  ───────────────────────── */

export default function ClientCompetitionShortlistScreen() {
  // Route guard — pre-approval cases bounce to /edit (nothing to shortlist).
  useRequireApprovedCase();
  const params = useLocalSearchParams<{ id: string }>();
  const competitionId = typeof params.id === "string" ? params.id : Array.isArray(params.id) ? params.id[0] : "";

  const [rows, setRows] = useState<ShortlistRow[]>([]);
  const [comp, setComp] = useState<ClientCompetitionListRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  // Hydrate persisted sort once.
  const sortHydratedRef = useRef(false);
  useEffect(() => {
    if (sortHydratedRef.current || !competitionId) return;
    sortHydratedRef.current = true;
    setSort(readSort(competitionId));
  }, [competitionId]);

  // Persist on change (after hydration).
  useEffect(() => {
    if (!sortHydratedRef.current || !competitionId) return;
    writeSort(competitionId, sort);
  }, [sort, competitionId]);

  // Debounce search 200ms.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);

  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [removeArmedId, setRemoveArmedId] = useState<string | null>(null);
  const [bulkRemoveArmed, setBulkRemoveArmed] = useState(false);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bulkRemoveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [compareOpen, setCompareOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!competitionId) return;
    setError(null);
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        listMyShortlist(competitionId),
        getClientCompetition(competitionId),
      ]);
      setRows(list);
      setComp(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load shortlist.");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  /* ─── Filter + sort ─── */
  const filtered = useMemo(() => {
    const q = searchDebounced.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (!q) return true;
      const hay = [
        r.student_full_name ?? "",
        r.student_university ?? "",
        r.student_major ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
    list.sort((a, b) => {
      switch (sort) {
        case "name":
          return (a.student_full_name ?? "").localeCompare(b.student_full_name ?? "");
        case "submissions":
          if (b.submission_count !== a.submission_count) return b.submission_count - a.submission_count;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "note": {
          const an = a.notes?.trim() ? 1 : 0;
          const bn = b.notes?.trim() ? 1 : 0;
          if (an !== bn) return bn - an;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
        case "recent":
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });
    return list;
  }, [rows, searchDebounced, sort]);

  /* ─── Aggregates for the hero subline ─── */
  const aggregates = useMemo(() => {
    if (rows.length === 0) return null;
    const dates = rows.map((r) => new Date(r.created_at).getTime()).filter((n) => !Number.isNaN(n));
    if (dates.length === 0) return null;
    const first = new Date(Math.min(...dates)).toISOString();
    const last = new Date(Math.max(...dates)).toISOString();
    return { first, last };
  }, [rows]);

  /* ─── Selection helpers ─── */
  const selectedCount = selected.size;
  const selectedRows = useMemo(() => filtered.filter((r) => selected.has(r.id)), [filtered, selected]);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelected(new Set());
    setBulkRemoveArmed(false);
  }

  /* ─── Row mutations ─── */
  async function onRemoveOne(rowId: string) {
    if (removeArmedId !== rowId) {
      setRemoveArmedId(rowId);
      if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
      removeTimerRef.current = setTimeout(() => setRemoveArmedId(null), 4000);
      return;
    }
    try {
      await removeFromShortlist(rowId);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
      if (expanded === rowId) setExpanded(null);
      if (editingNotes === rowId) setEditingNotes(null);
      setRemoveArmedId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't remove candidate.");
    }
  }

  async function onBulkRemove() {
    if (selectedRows.length === 0) return;
    if (!bulkRemoveArmed) {
      setBulkRemoveArmed(true);
      if (bulkRemoveTimerRef.current) clearTimeout(bulkRemoveTimerRef.current);
      bulkRemoveTimerRef.current = setTimeout(() => setBulkRemoveArmed(false), 4000);
      return;
    }
    try {
      await Promise.all(selectedRows.map((r) => removeFromShortlist(r.id)));
      clearSelection();
      setBulkActionsOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bulk remove failed.");
    }
  }

  function onBulkEmail() {
    const addrs = selectedRows.map((r) => r.student_email).filter(Boolean) as string[];
    if (addrs.length === 0) {
      setError("None of the selected candidates have an email on file.");
      setBulkActionsOpen(false);
      return;
    }
    const subject = encodeURIComponent(`${comp?.name ?? "Coravu"} — next steps`);
    const bcc = encodeURIComponent(addrs.join(","));
    const url = `mailto:?bcc=${bcc}&subject=${subject}`;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.location.href = url;
    }
    setBulkActionsOpen(false);
  }

  function onExport() {
    const safe = (comp?.name ?? "shortlist").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    exportToCsv(
      `${safe}_shortlist.csv`,
      ["name", "email", "university", "major", "graduation_year", "submissions", "final_score", "placement", "notes"],
      filtered.map((r) => [
        r.student_full_name ?? "",
        r.student_email ?? "",
        r.student_university ?? "",
        r.student_major ?? "",
        r.student_graduation_year ?? "",
        r.submission_count,
        r.final_score ?? "",
        r.placement ?? "",
        r.notes ?? "",
      ])
    );
  }

  /* ─── Render ─── */
  const filtersAreDefault = !searchDebounced.trim();
  const isTrulyEmpty = !loading && !error && rows.length === 0;

  if (isTrulyEmpty) {
    return <EmptyHero competitionId={competitionId} />;
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: PAGE_MAX_W }}>
          {/* Hero */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Shortlist
            </Text>
          </View>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.displayLight,
              fontSize: 30,
              lineHeight: 36,
              color: LINEN,
            }}
          >
            {comp?.name ?? "Competition"}
          </Text>
          <Text
            style={{
              marginTop: 4,
              fontFamily: FONTS.bodyLight,
              fontSize: 13,
              color: WM,
            }}
          >
            {rows.length === 0
              ? "No candidates yet."
              : `${rows.length} saved candidate${rows.length === 1 ? "" : "s"}${
                  aggregates
                    ? ` · added between ${fmtDate(aggregates.first)} and ${fmtDate(aggregates.last)}`
                    : ""
                }`}
          </Text>

          {/* Error banner */}
          {error ? (
            <View
              style={{
                marginTop: 18,
                paddingHorizontal: 14,
                paddingVertical: 10,
                borderWidth: 1,
                borderColor: RED_BORDER,
                backgroundColor: RED_BG,
                borderRadius: 4,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED_TEXT }}>
                {error}
              </Text>
            </View>
          ) : null}

          {/* Action toolbar */}
          <View
            style={{
              marginTop: 22,
              padding: 8,
              backgroundColor: FOREST_MID,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 6,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search shortlisted by name, school, or major…"
              placeholderTextColor="rgba(255,255,255,0.32)"
              style={
                {
                  width: 360,
                  flexShrink: 1,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  height: 40,
                  backgroundColor: BLACK,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 4,
                  color: W,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                } as unknown as TextStyle
              }
            />
            <SortPill
              value={sort}
              onChange={setSort}
              open={sortMenuOpen}
              setOpen={setSortMenuOpen}
            />

            <View style={{ flex: 1, minWidth: 0 }} />

            {selectedCount > 0 ? (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  borderRadius: 4,
                  backgroundColor: "rgba(181,135,58,0.10)",
                  borderWidth: 1,
                  borderColor: "rgba(181,135,58,0.28)",
                }}
              >
                <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, color: AMBER, letterSpacing: 1 }}>
                  {selectedCount} selected
                </Text>
                <Pressable onPress={clearSelection}>
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WS }}>
                    Clear
                  </Text>
                </Pressable>
              </View>
            ) : null}

            <ToolbarButton
              label={`Compare${selectedCount >= 2 ? ` (${selectedCount})` : ""}`}
              disabled={selectedCount < 2 || selectedCount > 4}
              onPress={() => setCompareOpen(true)}
            />
            <ToolbarButton label="Export CSV" onPress={onExport} disabled={filtered.length === 0} />
            <BulkActionsButton
              enabled={selectedCount > 0}
              open={bulkActionsOpen}
              setOpen={setBulkActionsOpen}
              bulkRemoveArmed={bulkRemoveArmed}
              selectedCount={selectedCount}
              onEmail={onBulkEmail}
              onRemove={onBulkRemove}
            />
          </View>

          {/* Body */}
          <View style={{ marginTop: 18 }}>
            {loading ? (
              <View style={{ gap: 8 }}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
                  <SkeletonRow key={k} />
                ))}
              </View>
            ) : filtered.length === 0 ? (
              <NoMatches
                onClear={() => {
                  setSearch("");
                  setSearchDebounced("");
                }}
                canClear={!filtersAreDefault}
              />
            ) : (
              <View style={{ borderRadius: 4, borderWidth: 1, borderColor: BORDER, overflow: "hidden" }}>
                {filtered.map((row, idx) => (
                  <CandidateRow
                    key={row.id}
                    row={row}
                    first={idx === 0}
                    selected={selected.has(row.id)}
                    expanded={expanded === row.id}
                    editingNotes={editingNotes === row.id}
                    hovered={hoveredId === row.id}
                    setHovered={(h) => setHoveredId(h ? row.id : null)}
                    onToggleSelect={() => toggleSelect(row.id)}
                    onExpand={() =>
                      setExpanded((cur) => (cur === row.id ? null : row.id))
                    }
                    onEditNotes={() => setEditingNotes(row.id)}
                    onCloseNotes={() => setEditingNotes(null)}
                    onView={() =>
                      router.push(
                        `/client/student/${row.student_id}?from=shortlist&competitionId=${encodeURIComponent(row.competition_id)}` as Href
                      )
                    }
                    onMessage={() => {
                      // TODO: route to internal DM flow when wired here.
                      console.warn("[Shortlist] Message: not implemented yet.", row.student_id);
                    }}
                    onRemove={() => void onRemoveOne(row.id)}
                    removeArmed={removeArmedId === row.id}
                    onNotesChange={async (notes) => {
                      try {
                        await updateShortlistNotes(row.id, notes);
                        setRows((prev) =>
                          prev.map((r) => (r.id === row.id ? { ...r, notes } : r))
                        );
                      } catch (e) {
                        setError(e instanceof Error ? e.message : "Couldn't save note.");
                      }
                    }}
                  />
                ))}
              </View>
            )}
          </View>
      </View>

      <CompareModal
        visible={compareOpen}
        onClose={() => setCompareOpen(false)}
        rows={selectedRows}
        onView={(studentId, competitionIdInner) =>
          router.push(
            `/client/student/${studentId}?from=shortlist&competitionId=${encodeURIComponent(competitionIdInner)}` as Href
          )
        }
      />
    </ScrollView>
  );
}

/* ─────────────────────────  Toolbar primitives  ───────────────────────── */

function ToolbarButton({
  label,
  onPress,
  disabled = false,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const web = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      {...(web ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const) : {})}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 8,
          height: 40,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: hover && !disabled ? "rgba(181,135,58,0.45)" : BORDER,
          backgroundColor: hover && !disabled ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)",
          opacity: disabled ? 0.4 : 1,
          alignItems: "center",
          justifyContent: "center",
        } as ViewStyle,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: hover && !disabled ? LINEN : WS,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SortPill({
  value,
  onChange,
  open,
  setOpen,
}: {
  value: SortKey;
  onChange: (v: SortKey) => void;
  open: boolean;
  setOpen: (b: boolean) => void;
}) {
  const wrapperRef = useRef<View | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 200,
  });

  function openMenu() {
    if (Platform.OS === "web" && wrapperRef.current) {
      const node = wrapperRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        setAnchor({
          top: Math.round(rect.bottom + 6),
          left: Math.round(rect.left),
          width: 220,
        });
      }
    }
    setOpen(true);
  }

  return (
    <View ref={wrapperRef} collapsable={false}>
      <Pressable
        onPress={openMenu}
        style={{
          height: 40,
          paddingHorizontal: 12,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: "rgba(255,255,255,0.02)",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyMedium,
            fontSize: 11,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: WS,
          }}
        >
          Sort: {SORT_LABELS[value]}
        </Text>
        <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
          <Path d="M3 4.5l3 3 3-3" stroke={WS} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
        </Svg>
      </Pressable>
      <RNModal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View
          style={{
            position: "absolute",
            top: anchor.top,
            left: anchor.left,
            width: anchor.width,
            backgroundColor: BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {SORT_KEYS.map((k) => {
            const active = k === value;
            return (
              <Pressable
                key={k}
                onPress={() => {
                  onChange(k);
                  setOpen(false);
                }}
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderLeftWidth: 2,
                  borderLeftColor: active ? AMBER : "transparent",
                  backgroundColor: active ? "rgba(181,135,58,0.10)" : "transparent",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyMedium,
                    fontSize: 12.5,
                    color: active ? LINEN : WS,
                  }}
                >
                  {SORT_LABELS[k]}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </RNModal>
    </View>
  );
}

function BulkActionsButton({
  enabled,
  open,
  setOpen,
  bulkRemoveArmed,
  selectedCount,
  onEmail,
  onRemove,
}: {
  enabled: boolean;
  open: boolean;
  setOpen: (b: boolean) => void;
  bulkRemoveArmed: boolean;
  selectedCount: number;
  onEmail: () => void;
  onRemove: () => void;
}) {
  const wrapperRef = useRef<View | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; right: number }>({ top: 0, right: 32 });

  function openMenu() {
    if (Platform.OS === "web" && wrapperRef.current) {
      const node = wrapperRef.current as unknown as { getBoundingClientRect?: () => DOMRect };
      const rect = node.getBoundingClientRect?.();
      if (rect && typeof window !== "undefined") {
        setAnchor({
          top: Math.round(rect.bottom + 6),
          right: Math.max(8, Math.round(window.innerWidth - rect.right)),
        });
      }
    }
    setOpen(true);
  }

  return (
    <View ref={wrapperRef} collapsable={false}>
      <ToolbarButton label={`Bulk actions ▾`} disabled={!enabled} onPress={openMenu} />
      <RNModal visible={open && enabled} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View
          style={{
            position: "absolute",
            top: anchor.top,
            right: anchor.right,
            width: 240,
            backgroundColor: BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <Pressable onPress={onEmail} style={{ paddingHorizontal: 14, paddingVertical: 12 }}>
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: LINEN }}>
              Email all selected
            </Text>
            <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 10.5, color: WM }}>
              Opens your mail client with BCC pre-filled.
            </Text>
          </Pressable>
          <View style={{ height: 1, backgroundColor: BORDER }} />
          <Pressable onPress={onRemove} style={{ paddingHorizontal: 14, paddingVertical: 12 }}>
            <Text
              style={{
                fontFamily: bulkRemoveArmed ? FONTS.bodyBold : FONTS.bodyMedium,
                fontSize: 12.5,
                color: RED,
                letterSpacing: bulkRemoveArmed ? 0.6 : 0,
              }}
            >
              {bulkRemoveArmed
                ? `Click again to remove ${selectedCount} candidate${selectedCount === 1 ? "" : "s"}`
                : "Remove from shortlist…"}
            </Text>
          </Pressable>
        </View>
      </RNModal>
    </View>
  );
}

/* ─────────────────────────  Row  ───────────────────────── */

function CandidateRow({
  row,
  first,
  selected,
  expanded,
  editingNotes,
  hovered,
  setHovered,
  onToggleSelect,
  onExpand,
  onEditNotes,
  onCloseNotes,
  onView,
  onMessage,
  onRemove,
  removeArmed,
  onNotesChange,
}: {
  row: ShortlistRow;
  first: boolean;
  selected: boolean;
  expanded: boolean;
  editingNotes: boolean;
  hovered: boolean;
  setHovered: (b: boolean) => void;
  onToggleSelect: () => void;
  onExpand: () => void;
  onEditNotes: () => void;
  onCloseNotes: () => void;
  onView: () => void;
  onMessage: () => void;
  onRemove: () => void;
  removeArmed: boolean;
  onNotesChange: (notes: string | null) => Promise<void>;
}) {
  const web = Platform.OS === "web";
  const showHoverActions = web && hovered;
  const subline = [row.student_major, row.student_university, row.student_graduation_year ? `Class of ${row.student_graduation_year}` : null]
    .filter(Boolean)
    .join(" · ");

  const noteSnippet = shortNote(row.notes ?? null);

  return (
    <View
      {...(web ? ({ onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) } as Record<string, () => void>) : {})}
      style={{
        borderTopWidth: first ? 0 : 1,
        borderTopColor: BORDER,
        backgroundColor: selected
          ? "rgba(181,135,58,0.10)"
          : web && hovered
            ? "rgba(181,135,58,0.04)"
            : "transparent",
        borderLeftWidth: 2,
        borderLeftColor: selected ? AMBER : "transparent",
      }}
    >
      <Pressable
        onPress={onExpand}
        accessibilityRole="button"
        accessibilityLabel={`Expand ${row.student_full_name ?? "candidate"}`}
        style={{
          height: 84,
          paddingHorizontal: 16,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        }}
      >
        {/* Checkbox */}
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            onToggleSelect();
          }}
          accessibilityLabel={`Select ${row.student_full_name ?? "candidate"}`}
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: selected ? AMBER : BORDER,
            backgroundColor: selected ? AMBER : "transparent",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selected ? (
            <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
              <Path d="M3 7.5l2.6 2.6 5.4-5.6" stroke={LINEN} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
          ) : null}
        </Pressable>

        {/* Avatar */}
        <Avatar avatarUrl={row.student_avatar_url ?? null} initials={makeInitials(row.student_full_name ?? null)} />

        {/* Label */}
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 14, color: LINEN }} numberOfLines={1}>
            {row.student_full_name ?? "Pending profile"}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={1}>
            {subline || "—"}
          </Text>
        </View>

        {/* Submissions chip */}
        <Text
          style={{
            fontFamily: FONTS.bodyMedium,
            fontSize: 11,
            color: row.submission_count === 0 ? RED : WM,
          }}
        >
          {row.submission_count === 0 ? "Not submitted yet" : `${row.submission_count} submitted`}
        </Text>

        {/* Notes preview / +Add note */}
        <View style={{ width: 240, alignItems: "flex-start" }}>
          {noteSnippet ? (
            <Text
              numberOfLines={1}
              style={{
                fontFamily: FONTS.bodyLight,
                fontStyle: "italic",
                fontSize: 11,
                color: WM,
              }}
            >
              {noteSnippet}
            </Text>
          ) : (
            <Pressable
              onPress={(e) => {
                e?.stopPropagation?.();
                onEditNotes();
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", color: AMBER }}>
                + Add note
              </Text>
            </Pressable>
          )}
        </View>

        {/* Hover actions */}
        {showHoverActions ? (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 14 }}>
            <RowAction
              label="View profile →"
              color={AMBER}
              onPress={() => {
                onView();
              }}
            />
            <RowAction label="Message" color={AMBER} onPress={onMessage} />
            <RowAction label="Edit notes" color={WS} onPress={onEditNotes} />
            <RowAction
              label={removeArmed ? "Click again to remove" : "Remove"}
              color={RED}
              onPress={onRemove}
            />
          </View>
        ) : null}
      </Pressable>

      {/* Inline notes editor */}
      {editingNotes ? (
        <NotesEditor
          initialValue={row.notes ?? ""}
          onSave={async (v) => {
            await onNotesChange(v.trim() ? v : null);
          }}
          onClose={onCloseNotes}
        />
      ) : null}

      {/* Expanded panel */}
      {expanded ? (
        <ExpandedPanel
          row={row}
          onClose={() => onExpand()}
          onSave={async (v) => {
            await onNotesChange(v.trim() ? v : null);
          }}
        />
      ) : null}
    </View>
  );
}

function RowAction({ label, color, onPress }: { label: string; color: string; onPress: () => void }) {
  return (
    <Pressable
      onPress={(e) => {
        e?.stopPropagation?.();
        onPress();
      }}
      accessibilityRole="link"
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function Avatar({ avatarUrl, initials }: { avatarUrl: string | null; initials: string }) {
  return (
    <View
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: FOREST_MID,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.22)",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {avatarUrl ? (
        <Image source={{ uri: avatarUrl }} style={{ width: 36, height: 36 }} resizeMode="cover" />
      ) : (
        <Text style={{ fontFamily: FONTS.displaySemiBold, fontSize: 14, color: AMBER }}>
          {initials}
        </Text>
      )}
    </View>
  );
}

/* ─────────────────────────  Notes editor (inline)  ───────────────────────── */

function NotesEditor({
  initialValue,
  onSave,
  onClose,
}: {
  initialValue: string;
  onSave: (v: string) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(initialValue);
  const [savedHint, setSavedHint] = useState(false);
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  // Debounced autosave. Excludes onSave from deps (stashed in ref) so a
  // parent re-render doesn't restart the timer mid-typing.
  useEffect(() => {
    if (draft === initialValue) return;
    const t = setTimeout(async () => {
      try {
        await onSaveRef.current(draft);
        setSavedHint(true);
        setTimeout(() => setSavedHint(false), 1200);
      } catch {
        /* parent surfaces error via setError */
      }
    }, NOTE_AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [draft, initialValue]);

  return (
    <View
      style={{
        paddingHorizontal: 16,
        paddingBottom: 14,
        paddingTop: 4,
      }}
    >
      <TextInput
        value={draft}
        onChangeText={setDraft}
        placeholder="Notes only you can see…"
        placeholderTextColor="rgba(255,255,255,0.32)"
        multiline
        textAlignVertical="top"
        style={
          {
            minHeight: 80,
            backgroundColor: BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            padding: 12,
            color: LINEN,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 20,
            ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
          } as unknown as TextStyle
        }
      />
      <View style={{ marginTop: 8, flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 11,
            color: savedHint ? SAGE : "transparent",
            ...(Platform.OS === "web" ? ({ transition: "color 200ms ease" } as object) : {}),
          }}
        >
          Saved
        </Text>
        <Pressable onPress={onClose}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Done
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

/* ─────────────────────────  Expanded panel  ───────────────────────── */

function ExpandedPanel({
  row,
  onClose,
  onSave,
}: {
  row: ShortlistRow;
  onClose: () => void;
  onSave: (v: string) => Promise<void>;
}) {
  return (
    <View
      style={{
        paddingHorizontal: 16,
        paddingBottom: 18,
        paddingTop: 4,
        backgroundColor: "rgba(255,255,255,0.02)",
        borderTopWidth: 1,
        borderTopColor: BORDER,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: WM,
          marginBottom: 8,
          marginTop: 12,
        }}
      >
        Notes
      </Text>
      <NotesEditor initialValue={row.notes ?? ""} onSave={onSave} onClose={onClose} />

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 14, marginTop: 12 }}>
        <PanelStat label="Submissions" value={String(row.submission_count)} />
        <PanelStat label="Final score" value={row.final_score == null ? "—" : row.final_score.toFixed(2)} />
        <PanelStat label="Placement" value={row.placement == null ? "—" : `#${row.placement}`} />
        <PanelStat label="Saved" value={fmtDate(row.created_at)} />
      </View>

      <Pressable
        onPress={onClose}
        style={{
          marginTop: 14,
          alignSelf: "flex-start",
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: BORDER,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: WS,
          }}
        >
          Close
        </Text>
      </Pressable>
    </View>
  );
}

function PanelStat({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ minWidth: 120 }}>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
      <Text style={{ marginTop: 4, fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
        {value}
      </Text>
    </View>
  );
}

/* ─────────────────────────  Compare modal  ───────────────────────── */

function CompareModal({
  visible,
  onClose,
  rows,
  onView,
}: {
  visible: boolean;
  onClose: () => void;
  rows: ShortlistRow[];
  onView: (studentId: string, competitionId: string) => void;
}) {
  // ESC to close (web).
  useEffect(() => {
    if (Platform.OS !== "web" || !visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  const cols = rows.length <= 2 ? 2 : rows.length === 3 ? 3 : 4;

  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        accessibilityLabel="Close compare"
        style={[
          { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(14,14,14,0.80)" },
          Platform.OS === "web"
            ? ({ backdropFilter: "blur(6px)" } as unknown as ViewStyle)
            : null,
        ]}
      />
      <View
        pointerEvents="box-none"
        style={{ flex: 1, alignItems: "center", justifyContent: "center", paddingHorizontal: 24, paddingVertical: 32 }}
      >
        <View
          style={[
            {
              width: "100%",
              maxWidth: 1200,
              maxHeight: Platform.OS === "web" ? ("82vh" as unknown as number) : 720,
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              overflow: "hidden",
            } as ViewStyle,
            Platform.OS === "web"
              ? ({ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.7)" } as unknown as ViewStyle)
              : null,
          ]}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 22,
              paddingVertical: 16,
              borderBottomWidth: 1,
              borderBottomColor: BORDER,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Compare {rows.length} candidate{rows.length === 1 ? "" : "s"}
            </Text>
            <Pressable
              onPress={onClose}
              accessibilityLabel="Close"
              style={{
                paddingHorizontal: 10,
                paddingVertical: 6,
                borderRadius: 4,
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: WS }}>
                Close ✕
              </Text>
            </Pressable>
          </View>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 16 }}>
              {rows.map((r) => (
                <CompareCard
                  key={r.id}
                  row={r}
                  cols={cols}
                  onView={() => {
                    onClose();
                    onView(r.student_id, r.competition_id);
                  }}
                />
              ))}
            </View>
          </ScrollView>
        </View>
      </View>
    </RNModal>
  );
}

function CompareCard({
  row,
  cols,
  onView,
}: {
  row: ShortlistRow;
  cols: number;
  onView: () => void;
}) {
  const subline = [row.student_major, row.student_university, row.student_graduation_year ? `Class of ${row.student_graduation_year}` : null]
    .filter(Boolean)
    .join(" · ");
  return (
    <View
      style={{
        flexBasis: (`calc(${100 / cols}% - ${(16 * (cols - 1)) / cols}px)` as unknown) as number,
        flexGrow: 1,
        minWidth: 240,
        padding: 16,
        backgroundColor: BLACK,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 6,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <Avatar avatarUrl={row.student_avatar_url ?? null} initials={makeInitials(row.student_full_name ?? null)} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }} numberOfLines={1}>
            {row.student_full_name ?? "Pending profile"}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={2}>
            {subline || "—"}
          </Text>
        </View>
      </View>

      <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
        <PanelStat label="Submissions" value={String(row.submission_count)} />
        <PanelStat label="Final score" value={row.final_score == null ? "—" : row.final_score.toFixed(2)} />
        <PanelStat label="Placement" value={row.placement == null ? "—" : `#${row.placement}`} />
      </View>

      <Text
        style={{
          marginTop: 14,
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
          marginBottom: 6,
        }}
      >
        Notes
      </Text>
      <Text
        style={{
          fontFamily: FONTS.bodyLight,
          fontSize: 12.5,
          lineHeight: 20,
          color: WS,
          minHeight: 40,
        }}
      >
        {row.notes?.trim() ? row.notes : "—"}
      </Text>

      <Pressable
        onPress={onView}
        accessibilityRole="link"
        style={{ marginTop: 14, alignSelf: "flex-start" }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          View full profile →
        </Text>
      </Pressable>
    </View>
  );
}

/* ─────────────────────────  Empty / no-match / skeleton  ───────────────────────── */

function EmptyHero({ competitionId }: { competitionId: string }) {
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 32,
        paddingVertical: 80,
      }}
    >
      <View style={{ width: "100%", maxWidth: 480, alignItems: "center" }}>
        <View style={{ opacity: 0.18 }}>
          <LogoMark size={48} tile={FOREST_MID} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
        </View>
        <Text style={{ marginTop: 18, fontFamily: FONTS.displayMedium, fontSize: 24, color: LINEN, textAlign: "center" }}>
          No candidates yet.
        </Text>
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            lineHeight: 22,
            color: WM,
            textAlign: "center",
            maxWidth: 380,
          }}
        >
          Star candidates from the Candidates tab to start building your shortlist.
        </Text>
        <Pressable
          onPress={() => router.push(`/client/competition/${competitionId}/candidates` as Href)}
          style={{
            marginTop: 20,
            paddingHorizontal: 16,
            paddingVertical: 11,
            backgroundColor: AMBER,
            borderRadius: 4,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: FOREST,
            }}
          >
            Browse candidates →
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

function NoMatches({ onClear, canClear }: { onClear: () => void; canClear: boolean }) {
  return (
    <View
      style={{
        padding: 22,
        backgroundColor: "rgba(28,43,26,0.40)",
        borderRadius: 4,
        borderWidth: 1,
        borderColor: BORDER,
        alignItems: "center",
      }}
    >
      <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: LINEN }}>
        No shortlisted candidates match these filters.
      </Text>
      {canClear ? (
        <Pressable onPress={onClear} style={{ marginTop: 8 }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Clear filters
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

/**
 * Local dark-theme skeleton block — pulses opacity 0.5 → 0.85 on a 1.1s loop.
 * Replaces the legacy `components/ui/Skeleton` whose SAND_DARK fill rendered
 * incorrectly against the BLACK portal background.
 */
function Skeleton({
  width,
  height = 14,
  radius = 4,
  style,
}: {
  width?: number | string;
  height?: number;
  radius?: number;
  style?: ViewStyle;
}) {
  const opacity = useRef(new Animated.Value(0.5)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 0.85,
          duration: 1100,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(opacity, {
          toValue: 0.5,
          duration: 1100,
          useNativeDriver: Platform.OS !== "web",
        }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={[
        {
          width: (width as unknown as number) ?? "100%",
          height,
          borderRadius: radius,
          backgroundColor: FOREST_MID,
          opacity,
        },
        style,
      ]}
    />
  );
}

function SkeletonRow() {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingHorizontal: 16,
        height: 84,
        backgroundColor: "rgba(28,43,26,0.40)",
        borderRadius: 4,
        borderWidth: 1,
        borderColor: BORDER,
      }}
    >
      <Skeleton width={24} height={24} radius={4} />
      <Skeleton width={36} height={36} radius={18} />
      <View style={{ flex: 1 }}>
        <Skeleton width={"50%" as unknown as number} height={13} />
        <Skeleton width={"75%" as unknown as number} height={11} style={{ marginTop: 8 }} />
      </View>
      <Skeleton width={70} height={11} />
      <Skeleton width={140} height={11} />
    </View>
  );
}

void WL;
void RED_BG;
void RED_BORDER;
void RED_TEXT;
